import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import { API_URL } from "../../Constants";
import { toast } from "react-toastify";
import { PlusIcon, XMarkIcon, ArrowPathIcon } from "@heroicons/react/24/solid";
import { FaInfoCircle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import ConfirmModal from "./ConfirmModal";

const IVR_GURU_LOGO = "https://www.ivrguru.com/static/media/ivr-new-logo.b6ddcce48a92fedad97f.png";

// IVR GURU's reconciliation/polling base URL is fixed for every customer - only the
// API key differs per company. Replace this with IVR GURU's actual documented
// endpoint before shipping; keeping it as a named constant (rather than a form field)
// is what makes this a true one-click connect instead of asking the user to know
// their own vendor's API URL.
const IVR_GURU_BASE_URL = "https://api.dndfilter.com/api/key/getCallRecords"; // TODO: confirm with IVR GURU

const emptyRow = () => ({ didNumber: "", projectName: "" });

const maskKey = (key) => {
  if (!key) return "";
  if (key.length <= 8) return "••••••••";
  return `${key.slice(0, 4)}••••••••${key.slice(-4)}`;
};

const IvrGuruIntegration = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const authHeader = { Authorization: `Bearer ${localStorage.getItem("token")}` };
  const companyId = user?.accountType === "COMPANY" ? user?.id : user?.companyId;

  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null); // the single IVRApiKey record for this company, or null
  const [projects, setProjects] = useState([]);

  // connect / rotate key
  const [apiKeyInput, setApiKeyInput] = useState("");
  const [editingKey, setEditingKey] = useState(false);
  const [connecting, setConnecting] = useState(false);

  // DID -> project mapping table (local editable copy of config.uniqueName)
  const [rows, setRows] = useState([emptyRow()]);
  const [savingMapping, setSavingMapping] = useState(false);

  const [syncing, setSyncing] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const [showInstructions, setShowInstructions] = useState(false);

  useEffect(() => {
    if (companyId) {
      fetchConfig();
      fetchProjects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/projectName/company/${companyId}`, { headers: authHeader });
      setProjects(res.data || []);
    } catch (error) {
      console.error("Failed to fetch projects:", error);
      setProjects([]);
    }
  };

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/IvrApiKey/company/${companyId}`, { headers: authHeader });
      const existing = res.data?.apiKey?.[0] || null;
      setConfig(existing);
      setRows(existing?.uniqueName?.length ? existing.uniqueName : [emptyRow()]);
    } catch (error) {
      // 404 just means "not connected yet" - not a real error
      if (error?.response?.status !== 404) {
        toast.error("Failed to load IVR GURU integration");
      }
      setConfig(null);
      setRows([emptyRow()]);
    } finally {
      setLoading(false);
    }
  };

  /** ---------------------------- connect / key ---------------------------- */

  const handleConnect = async () => {
    if (!apiKeyInput.trim()) {
      toast.error("Enter your IVR GURU API key");
      return;
    }
    try {
      setConnecting(true);
      await axios.post(
        `${API_URL}/api/IvrApiKey`,
        {
          companyId,
          apikey: apiKeyInput.trim(),
          ivrUrl: IVR_GURU_BASE_URL,
          ivrProviderName: "IVR GURU",
          uniqueName: [],
        },
        { headers: authHeader }
      );
      toast.success("IVR GURU connected");
      setApiKeyInput("");
      await fetchConfig();
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to connect IVR GURU");
    } finally {
      setConnecting(false);
    }
  };

  const handleUpdateKey = async () => {
    if (!apiKeyInput.trim()) {
      toast.error("Enter the new API key");
      return;
    }
    try {
      setConnecting(true);
      await axios.put(
        `${API_URL}/api/IvrApiKey/${config.id}`,
        { companyId, apikey: apiKeyInput.trim() },
        { headers: authHeader }
      );
      toast.success("API key updated");
      setApiKeyInput("");
      setEditingKey(false);
      await fetchConfig();
    } catch {
      toast.error("Failed to update API key");
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      setDisconnecting(true);
      await axios.delete(`${API_URL}/api/IvrApiKey/${config.id}`, { headers: authHeader });
      toast.success("IVR GURU disconnected");
      setConfig(null);
      setRows([emptyRow()]);
    } catch {
      toast.error("Failed to disconnect IVR GURU");
    } finally {
      setDisconnecting(false);
      setShowDisconnectModal(false);
    }
  };

  const handleSyncNow = async () => {
    try {
      setSyncing(true);
      await axios.post(`${API_URL}/api/ivr/sync-call-records/${companyId}`, {}, { headers: authHeader });
      toast.success("Sync started - any missed calls will appear shortly");
    } catch {
      toast.error("Failed to trigger sync");
    } finally {
      setSyncing(false);
    }
  };

  /** ---------------------------- DID -> project mapping ---------------------------- */

  const handleRowChange = (idx, field, value) => {
    setRows((prev) => prev.map((row, i) => (i === idx ? { ...row, [field]: value } : row)));
  };

  const handleProjectSelect = (idx, value) => {
    if (value === "add_new") {
      navigate("/leadLabel");
      return;
    }
    handleRowChange(idx, "projectName", value);
  };

  const addRow = () => setRows((prev) => [...prev, emptyRow()]);

  const removeRow = (idx) => {
    setRows((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      return next.length > 0 ? next : [emptyRow()];
    });
  };

  const saveMapping = async () => {
    const cleanRows = rows.filter((r) => r.didNumber.trim());
    try {
      setSavingMapping(true);
      await axios.put(
        `${API_URL}/api/IvrApiKey/${config.id}`,
        { companyId, uniqueName: cleanRows },
        { headers: authHeader }
      );
      toast.success("DID mapping saved");
      await fetchConfig();
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to save DID mapping");
    } finally {
      setSavingMapping(false);
    }
  };

  /** ---------------------------- render ---------------------------- */

  if (loading) {
    return <div className="p-6 text-sm text-gray-600 dark:text-gray-300">Loading...</div>;
  }

  if (!config) {
    return (
      <div className="max-w-md mx-auto bg-white dark:bg-gray-800 rounded-xl border p-8 text-center cfg-font">
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@600;700&family=Inter:wght@400;500;600&display=swap'); .cfg-heading{font-family:'Sora',sans-serif;} .cfg-font{font-family:'Inter',sans-serif;}`}</style>
        <img src={IVR_GURU_LOGO} alt="IVR GURU" className="h-10 mx-auto mb-4 object-contain" />
        <h3 className="text-lg font-semibold cfg-heading mb-1">Connect IVR GURU</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
          Paste your IVR GURU API key to start pulling in leads automatically. You can map DID numbers to
          projects right after connecting.
        </p>
        <input
          type="password"
          placeholder="IVR GURU API key"
          value={apiKeyInput}
          onChange={(e) => setApiKeyInput(e.target.value)}
          className="w-full p-2 border border-[#E5E7EB] rounded mb-4 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#E9D5FF] focus:border-[#673AB7]"
        />
        <button
          onClick={handleConnect}
          disabled={connecting}
          className="w-full px-4 py-2 bg-[#673AB7] text-white rounded hover:bg-[#5E35A6] disabled:opacity-50"
        >
          {connecting ? "Connecting..." : "Connect"}
        </button>
      </div>
    );
  }

  return (
    <div className="cfg-font">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@600;700&family=Inter:wght@400;500;600&display=swap'); .cfg-heading{font-family:'Sora',sans-serif;} .cfg-font{font-family:'Inter',sans-serif;}`}</style>
      {/* connection status card */}
      <div className="mb-6 p-4 bg-white dark:bg-gray-800 rounded border flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <img src={IVR_GURU_LOGO} alt="IVR GURU" className="h-8 object-contain" />
          <div>
            <div className="flex items-center gap-2">
              <span className="font-semibold">IVR GURU</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200">
                Connected
              </span>
            </div>
            {!editingKey && <p className="text-xs text-gray-500 mt-0.5">API key: {maskKey(config.apikey)}</p>}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleSyncNow}
            disabled={syncing}
            className="flex items-center gap-1 px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
            title="Pull in any calls the webhook might have missed"
          >
            <ArrowPathIcon className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Syncing..." : "Sync now"}
          </button>
          <button
            onClick={() => setEditingKey((v) => !v)}
            className="px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
          >
            {editingKey ? "Cancel" : "Update key"}
          </button>
          <button
            onClick={() => setShowDisconnectModal(true)}
            className="px-3 py-2 text-sm bg-red-50 text-red-600 dark:bg-red-900 dark:text-red-200 rounded hover:bg-red-100 dark:hover:bg-red-800"
          >
            Disconnect
          </button>
        </div>
      </div>

      {editingKey && (
        <div className="mb-6 p-4 bg-white dark:bg-gray-800 rounded border flex gap-3 items-center">
          <input
            type="password"
            placeholder="New IVR GURU API key"
            value={apiKeyInput}
            onChange={(e) => setApiKeyInput(e.target.value)}
            className="flex-1 p-2 border border-[#E5E7EB] rounded dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#E9D5FF] focus:border-[#673AB7]"
          />
          <button
            onClick={handleUpdateKey}
            disabled={connecting}
            className="px-4 py-2 bg-[#673AB7] text-white rounded hover:bg-[#5E35A6] disabled:opacity-50"
          >
            Save
          </button>
        </div>
      )}

      {/* DID -> project mapping */}
      <div className="p-4 bg-white dark:bg-gray-800 rounded border">
        <h4 className="text-sm font-semibold cfg-heading mb-3">DID numbers &amp; projects</h4>
        <p className="text-xs text-gray-500 mb-4">
          Every DID number below maps to a project. New leads coming in on a mapped number get that project
          automatically.
        </p>

        {rows.map((row, idx) => (
          <div key={idx} className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-2 relative">
            {rows.length > 1 && (
              <button
                onClick={() => removeRow(idx)}
                className="absolute -right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-red-500"
                title="Remove this mapping"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
            <div>
              <label className="block text-sm font-medium mb-1">DID Number</label>
              <input
                placeholder="Enter DID Number"
                value={row.didNumber}
                onChange={(e) => handleRowChange(idx, "didNumber", e.target.value)}
                className="w-full p-2 border rounded dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Project Name</label>
              <select
                value={row.projectName}
                onChange={(e) => handleProjectSelect(idx, e.target.value)}
                className="w-full p-2 border rounded dark:bg-gray-700 dark:text-white"
              >
                <option value="">Select Project</option>
                {projects.length === 0 ? (
                  <option value="add_new">No projects available - Add New</option>
                ) : (
                  <>
                    {projects.map((project) => (
                      <option key={project.id} value={project.name}>
                        {project.name}
                      </option>
                    ))}
                    <option value="add_new">+ Add New Project</option>
                  </>
                )}
              </select>
            </div>
          </div>
        ))}

        <button onClick={addRow} className="text-[#673AB7] text-sm mt-2 hover:underline flex items-center">
          <PlusIcon className="h-4 w-4 mr-1" />
          Add DID mapping
        </button>

        <div className="mt-4">
          <button
            onClick={saveMapping}
            disabled={savingMapping}
            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
          >
            {savingMapping ? "Saving..." : "Save mapping"}
          </button>
        </div>
      </div>

      <ConfirmModal
        open={showDisconnectModal}
        title="Disconnect IVR GURU?"
        message="This removes your API key and DID mapping. Leads already created won't be affected, but new calls won't be pulled in until you reconnect."
        confirmLabel="Disconnect"
        loading={disconnecting}
        onCancel={() => setShowDisconnectModal(false)}
        onConfirm={handleDisconnect}
      />
    </div>
  );
};

export default IvrGuruIntegration;